import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Shield, Menu, CheckSquare, Square } from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const UserTypeMenuAccess = () => {
  const [accessList, setAccessList] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [filteredAccessList, setFilteredAccessList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUserTypeId, setFilterUserTypeId] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    accessid: null,
    usertypeid: '',
    menuid: '',
    canview: true,
    canadd: false,
    canedit: false,
    candelete: false
  });
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchAccessList();
    fetchUserTypes();
    fetchMenuItems();
  }, []);

  useEffect(() => {
    filterAccessList();
  }, [searchTerm, filterUserTypeId, accessList]);

  const fetchAccessList = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/UserTypeMenuAccess.php');
      
      if (response.data.success) {
        setAccessList(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching menu access:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserTypes = async () => {
    try {
      const response = await apiClient.get('/UserTypes.php', {
        params: { active: true }
      });
      
      if (response.data.success) {
        setUserTypes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching user types:', error);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await apiClient.get('/MenuItems.php', {
        params: { active: true }
      });
      
      if (response.data.success) {
        setMenuItems(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching menu items:', error);
    }
  };

  const filterAccessList = () => {
    let filtered = accessList;

    // Filter by user type
    if (filterUserTypeId) {
      filtered = filtered.filter(access => access.usertypeid === parseInt(filterUserTypeId));
    }

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(access =>
        access.usertypename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        access.menuname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        access.menucode.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredAccessList(filtered);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.usertypeid) {
      newErrors.usertypeid = 'User type is required';
    }
    
    if (!formData.menuid) {
      newErrors.menuid = 'Menu is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/UserTypeMenuAccess.php', {
        ...formData,
        usertypeid: parseInt(formData.usertypeid),
        menuid: parseInt(formData.menuid),
        canview: formData.canview ? 1 : 0,
        canadd: formData.canadd ? 1 : 0,
        canedit: formData.canedit ? 1 : 0,
        candelete: formData.candelete ? 1 : 0
      });
      
      const data = response.data;
      
      if (data.success) {
        alert(editMode ? 'Menu access updated successfully!' : 'Menu access created successfully!');
        setShowModal(false);
        resetForm();
        fetchAccessList();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving menu access:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else if (error.response?.status === 409) {
        alert('Menu access already exists for this user type.');
      } else {
        alert('Error saving menu access. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (access) => {
    setFormData({
      accessid: access.accessid,
      usertypeid: access.usertypeid,
      menuid: access.menuid,
      canview: access.canview === 1,
      canadd: access.canadd === 1,
      canedit: access.canedit === 1,
      candelete: access.candelete === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this menu access?')) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/UserTypeMenuAccess.php', {
        data: { accessid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('Menu access removed successfully!');
        fetchAccessList();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting menu access:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error removing menu access. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      accessid: null,
      usertypeid: '',
      menuid: '',
      canview: true,
      canadd: false,
      canedit: false,
      candelete: false
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const PermissionIcon = ({ allowed }) => {
    return allowed ? (
      <CheckSquare size={18} style={{ color: '#10b981' }} />
    ) : (
      <Square size={18} style={{ color: '#d1d5db' }} />
    );
  };

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>
            <Shield size={32} style={{ display: 'inline', marginRight: '10px', verticalAlign: 'middle' }} />
            User Type Menu Access
          </h2>
          <p className="subtitle">Manage menu permissions for user types</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Permission
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by user type or menu name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <select
            value={filterUserTypeId}
            onChange={(e) => setFilterUserTypeId(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '0.95rem'
            }}
          >
            <option value="">All User Types</option>
            {userTypes.map(type => (
              <option key={type.usertypeid} value={type.usertypeid}>
                {type.usertypename}
              </option>
            ))}
          </select>
          <div className="toolbar-info">
            Total: {filteredAccessList.length} {filteredAccessList.length === 1 ? 'permission' : 'permissions'}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading menu access permissions...</div>
      ) : filteredAccessList.length === 0 ? (
        <div className="empty-state">
          <p>No menu access permissions found</p>
          {(searchTerm || filterUserTypeId) && (
            <button onClick={() => { setSearchTerm(''); setFilterUserTypeId(''); }}>Clear filters</button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>User Type</th>
                <th>Menu</th>
                <th style={{ textAlign: 'center' }}>View</th>
                <th style={{ textAlign: 'center' }}>Add</th>
                <th style={{ textAlign: 'center' }}>Edit</th>
                <th style={{ textAlign: 'center' }}>Delete</th>
                <th>Created At</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccessList.map((access) => (
                <tr key={access.accessid}>
                  <td>
                    <span className="country-badge">
                      {access.usertypename}
                    </span>
                  </td>
                  <td className="font-semibold">
                    {access.menucode} - {access.menuname}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <PermissionIcon allowed={access.canview === 1} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <PermissionIcon allowed={access.canadd === 1} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <PermissionIcon allowed={access.canedit === 1} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <PermissionIcon allowed={access.candelete === 1} />
                  </td>
                  <td>{new Date(access.createdat).toLocaleDateString()}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(access)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(access.accessid)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Menu Permission' : 'Add Menu Permission'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="usertypeid">
                    User Type <span className="required">*</span>
                  </label>
                  <select
                    id="usertypeid"
                    value={formData.usertypeid}
                    onChange={(e) => setFormData({ ...formData, usertypeid: e.target.value })}
                    className={errors.usertypeid ? 'error' : ''}
                    disabled={editMode}
                  >
                    <option value="">-- Select User Type --</option>
                    {userTypes.map(type => (
                      <option key={type.usertypeid} value={type.usertypeid}>
                        {type.usertypename}
                      </option>
                    ))}
                  </select>
                  {errors.usertypeid && (
                    <span className="error-message">{errors.usertypeid}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="menuid">
                    Menu <span className="required">*</span>
                  </label>
                  <select
                    id="menuid"
                    value={formData.menuid}
                    onChange={(e) => setFormData({ ...formData, menuid: e.target.value })}
                    className={errors.menuid ? 'error' : ''}
                    disabled={editMode}
                  >
                    <option value="">-- Select Menu --</option>
                    {menuItems.map(menu => (
                      <option key={menu.menuid} value={menu.menuid}>
                        {menu.menucode} - {menu.menuname}
                      </option>
                    ))}
                  </select>
                  {errors.menuid && (
                    <span className="error-message">{errors.menuid}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '12px', fontWeight: 600, color: '#374151' }}>
                    Permissions
                  </label>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.canview}
                        onChange={(e) => setFormData({ ...formData, canview: e.target.checked })}
                      />
                      <span>Can View</span>
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.canadd}
                        onChange={(e) => setFormData({ ...formData, canadd: e.target.checked })}
                      />
                      <span>Can Add</span>
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.canedit}
                        onChange={(e) => setFormData({ ...formData, canedit: e.target.checked })}
                      />
                      <span>Can Edit</span>
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.candelete}
                        onChange={(e) => setFormData({ ...formData, candelete: e.target.checked })}
                      />
                      <span>Can Delete</span>
                    </label>
                  </div>

                  <small style={{ color: '#6B7280', marginTop: '12px', display: 'block' }}>
                    Select which operations this user type can perform on this menu
                  </small>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTypeMenuAccess;